import { type IWebAnalyticsEntityHistory } from '@mahawi/eshop-common';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type IAdminCategory,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import { type IWebAnalyticsEntry } from '../product/types';
import { type ICategoryState } from './types';

export const preloadedState: ICategoryState = {
  ...asyncStateInitial,
  category: undefined,
  webAnalytics: undefined,
  webAnalyticsInProcess: false,
  webAnalyticsDaysInterval: 30,
};

const setCategoryResponse = (
  state: ICategoryState,
  category: IAdminCategory | null,
): void => {
  category?.property.codes.sort((a: string, b: string): number =>
    stringCompare(a, b),
  );

  state.category = category;
  finishLoading(state);
};

const categorySlice = createSlice({
  name: 'Category',
  initialState: preloadedState,
  reducers: {
    categoryLoad: {
      reducer(state) {
        startLoading(state);
        state.category = undefined;
      },
      prepare(categoryUUID: string) {
        return { payload: { category: { uuid: categoryUUID } } };
      },
    },
    categoryLoadResponse: {
      reducer(state, action: { payload: { category: IAdminCategory | null } }) {
        setCategoryResponse(state, action.payload.category);
      },
      prepare(category: IAdminCategory | null) {
        return { payload: { category } };
      },
    },
    nameUpdate: {
      reducer(state) {
        startLoading(state);
      },
      prepare(categoryUUID: string, name: string, languageTypeCode: string) {
        return {
          payload: {
            category: { uuid: categoryUUID },
            languageType: { code: languageTypeCode },
            name,
          },
        };
      },
    },
    nameUpdateResponse: {
      reducer(state, action: { payload: { category: IAdminCategory | null } }) {
        setCategoryResponse(state, action.payload.category);
      },
      prepare(category: IAdminCategory | null) {
        return { payload: { category } };
      },
    },
    nameLive: {
      reducer(
        state,
        action: { payload: { languageType: { code: string }; name: string } },
      ) {
        const { payload } = action;
        const name: ITranslation | undefined = state.category?.names.find(
          ({ code }: ITranslation): boolean =>
            code === payload.languageType.code,
        );

        if (name) {
          name.value = payload.name;
        } else {
          state.category?.names.push({
            code: payload.languageType.code,
            value: payload.name,
          });
        }
      },
      prepare(name: string, languageTypeCode: string) {
        return { payload: { languageType: { code: languageTypeCode }, name } };
      },
    },
    savePropertyCodes: {
      reducer(state) {
        startLoading(state);
      },
      prepare(categoryUUID: string, propertyCodes: string[]) {
        return {
          payload: {
            category: { uuid: categoryUUID },
            property: { codes: propertyCodes },
          },
        };
      },
    },
    categorySavePosition: {
      reducer(state) {
        startLoading(state);
      },
      prepare(categoryUUID: string, position: number) {
        return {
          payload: { category: { uuid: categoryUUID }, position },
        };
      },
    },
    categorySavePositionResponse: {
      reducer(state) {
        finishLoading(state);
      },
      prepare(saved: boolean) {
        return { payload: { saved } };
      },
    },
    categorySaveChildCategories: {
      reducer(state) {
        startLoading(state);
      },
      prepare(categoryUUID: string, childCategoriesUUIDs: string[]) {
        return {
          payload: {
            category: { uuid: categoryUUID },
            childCategories: { uuids: childCategoriesUUIDs },
          },
        };
      },
    },
    categorySaveChildCategoriesResponse: {
      reducer(state, action: { payload: { category: IAdminCategory | null } }) {
        state.category = action.payload.category;
        finishLoading(state);
      },
      prepare(category: IAdminCategory | null) {
        return { payload: { category } };
      },
    },
    categorySetEndPriceMarginRate: {
      reducer(
        state,
        action: {
          payload: { category: { uuid: string }; endPriceMarginRate: number };
        },
      ) {
        if (state.category) {
          state.category.endPriceMarginRate = action.payload.endPriceMarginRate;
        }
        state.inProcess = true;
      },
      prepare(categoryUUID: string, endPriceMarginRate: number) {
        return {
          payload: {
            category: { uuid: categoryUUID },
            endPriceMarginRate,
          },
        };
      },
    },
    slugLive: {
      reducer(
        state,
        action: { payload: { languageType: { code: string }; slug: string } },
      ) {
        const { payload } = action;
        const slug: ITranslation | undefined = state.category?.slugs.find(
          ({ code }: ITranslation): boolean =>
            code === payload.languageType.code,
        );

        if (slug) {
          slug.value = payload.slug;
        } else {
          state.category?.slugs.push({
            code: payload.languageType.code,
            value: payload.slug,
          });
        }
      },
      prepare(slug: string, languageTypeCode: string) {
        return { payload: { languageType: { code: languageTypeCode }, slug } };
      },
    },
    slugUpdate: {
      reducer(state) {
        startLoading(state);
      },
      prepare(categoryUUID: string, slug: string, languageTypeCode: string) {
        return {
          payload: {
            category: { uuid: categoryUUID },
            languageType: { code: languageTypeCode },
            slug,
          },
        };
      },
    },
    slugUpdateResponse: {
      reducer(state, action: { payload: { category: IAdminCategory | null } }) {
        setCategoryResponse(state, action.payload.category);
      },
      prepare(category: IAdminCategory | null) {
        return { payload: { category } };
      },
    },
    slugsUpdate: {
      reducer(state) {
        startLoading(state);
      },
      prepare(
        categoryUUID: string,
        slugs: { slug: string; languageTypeCode: string }[],
      ) {
        return {
          payload: {
            category: { uuid: categoryUUID },
            slugs,
          },
        };
      },
    },
    slugsUpdateResponse: {
      reducer(state, action: { payload: { category: IAdminCategory | null } }) {
        setCategoryResponse(state, action.payload.category);
      },
      prepare(category: IAdminCategory | null) {
        return { payload: { category } };
      },
    },
    namesDescriptionsSlugsUpdate: {
      reducer(state) {
        startLoading(state);
      },
      prepare(
        categoryUUID: string,
        names: ITranslation[],
        descriptions: ITranslation[],
        slugs: ITranslation[],
      ) {
        return {
          payload: {
            category: { uuid: categoryUUID },
            names,
            descriptions,
            slugs,
          },
        };
      },
    },
    namesDescriptionsSlugsUpdateResponse: {
      reducer(state) {
        finishLoading(state);
      },
      prepare(updated: boolean) {
        return { payload: { updated } };
      },
    },
    seoLive: {
      reducer(
        state,
        action: {
          payload: {
            languageType: { code: string };
            description: string;
            metaDescription: string;
            metaKeywords: string[];
          };
        },
      ) {
        if (!state.category) {
          return;
        }

        const { code } = action.payload.languageType;

        const upsertString = (arr: ITranslation[], value: string): void => {
          const existing = arr.find((t) => t.code === code);
          if (existing) {
            existing.value = value;
          } else {
            arr.push({ code, value });
          }
        };

        const upsertKeywords = (
          arr: { code: string; value: string[] }[],
          value: string[],
        ): void => {
          const existing = arr.find((t) => t.code === code);
          if (existing) {
            existing.value = value;
          } else {
            arr.push({ code, value });
          }
        };

        upsertString(state.category.descriptions, action.payload.description);
        upsertString(
          state.category.metaDescriptions,
          action.payload.metaDescription,
        );
        upsertKeywords(
          state.category.metaKeywords,
          action.payload.metaKeywords,
        );
      },
      prepare(
        languageTypeCode: string,
        description: string,
        metaDescription: string,
        metaKeywords: string[],
      ) {
        return {
          payload: {
            languageType: { code: languageTypeCode },
            description,
            metaDescription,
            metaKeywords,
          },
        };
      },
    },
    seoUpdate: {
      reducer(state) {
        startLoading(state);
      },
      prepare(
        categoryUUID: string,
        languageTypeCode: string,
        description: string,
        metaDescription: string,
        metaKeywords: string[],
      ) {
        return {
          payload: {
            category: { uuid: categoryUUID },
            languageType: { code: languageTypeCode },
            description,
            metaDescription,
            metaKeywords,
          },
        };
      },
    },
    seoUpdateResponse: {
      reducer(state, action: { payload: { category: IAdminCategory | null } }) {
        setCategoryResponse(state, action.payload.category);
      },
      prepare(category: IAdminCategory | null) {
        return { payload: { category } };
      },
    },
    categoryWebAnalyticsLoad: {
      reducer(
        state,
        action: {
          payload: { category: { uuid: string }; daysInterval: number };
        },
      ) {
        state.webAnalyticsInProcess = true;
        state.webAnalyticsDaysInterval = action.payload.daysInterval;
      },
      prepare(categoryUUID: string, daysInterval: number) {
        return { payload: { category: { uuid: categoryUUID }, daysInterval } };
      },
    },
    categoryWebAnalyticsLoadResponse: {
      reducer(
        state,
        action: {
          payload: {
            webAnalytics: IWebAnalyticsEntry[];
            webAnalyticsHistory?: IWebAnalyticsEntityHistory;
          };
        },
      ) {
        state.webAnalytics = action.payload.webAnalytics;
        state.webAnalyticsHistory = action.payload.webAnalyticsHistory;
        state.webAnalyticsInProcess = false;
      },
      prepare(
        webAnalytics: IWebAnalyticsEntry[],
        webAnalyticsHistory?: IWebAnalyticsEntityHistory,
      ) {
        return { payload: { webAnalytics, webAnalyticsHistory } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Category'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  categoryLoad,
  categoryLoadResponse,
  nameUpdate,
  nameUpdateResponse,
  nameLive,
  savePropertyCodes,
  categorySavePosition,
  categorySavePositionResponse,
  categorySaveChildCategories,
  categorySaveChildCategoriesResponse,
  categorySetEndPriceMarginRate,
  slugLive,
  slugUpdate,
  slugUpdateResponse,
  slugsUpdate,
  slugsUpdateResponse,
  namesDescriptionsSlugsUpdate,
  namesDescriptionsSlugsUpdateResponse,
  seoLive,
  seoUpdate,
  seoUpdateResponse,
  categoryWebAnalyticsLoad,
  categoryWebAnalyticsLoadResponse,
} = categorySlice.actions;
export default categorySlice.reducer;
